import {
  createMinMaxValidation,
  createOptionalNumericValidationWithMax,
} from "@cma/shared/helpers/yup-extensions";
import * as yup from "yup";
import { INITIAL_SORT_OPTION } from "../../../shared/helpers/property-search/comparable-sort-options";
import { FindListingsAv8RequestWaterfrontClassificationEnum } from "@shared/apis/sidekick/generated";

export const PRICE_MAX_VALUE = 999_999_999;
export const LIVING_SPACE_MAX_VALUE = 999_999;
export const LOT_SIZE_MAX_VALUE = 999_999_999;
export const GARAGE_SPACES_MAX_VALUE = 999;
export const BEDS_BATHS_MAX_VALUE = 99;

export const PROPERTY_TYPE_ALL_SALE_ID = "Sale > *";
export const PROPERTY_TYPE_ALL_LEASE_ID = "Lease > *";

export const isNumberAllowed = (
  value: number | undefined = 0,
  maxValue: number,
) => {
  return value >= 0 && value <= maxValue;
};

export const findListingsFiltersFormSchema = yup.object().shape({
  dealType: yup
    .string()
    .oneOf(["Sale", "Lease"], "Must be Sale or Lease")
    .default("Sale")
    .label("Deal Type"),
  propertyTypes: yup
    .array()
    .of(yup.string().required())
    .optional()
    .label("Property Types"),
  propertyStatus: yup
    .array()
    .of(yup.string().required())
    .optional()
    .label("Property Status"),
  priceMin: createOptionalNumericValidationWithMax("Price", PRICE_MAX_VALUE)
    .optional()
    .label("Min Price")
    .concat(createMinMaxValidation("priceMax")),
  priceMax: createOptionalNumericValidationWithMax("Price", PRICE_MAX_VALUE)
    .optional()
    .label("Max Price"),
  bedsMin: createOptionalNumericValidationWithMax(
    "Bedrooms",
    BEDS_BATHS_MAX_VALUE,
  )
    .optional()
    .label("Min Bedrooms")
    .concat(createMinMaxValidation("bedsMax")),
  bedsMax: createOptionalNumericValidationWithMax(
    "Bedrooms",
    BEDS_BATHS_MAX_VALUE,
  )
    .optional()
    .label("Max Bedrooms"),
  bathsMin: createOptionalNumericValidationWithMax(
    "Bathrooms",
    BEDS_BATHS_MAX_VALUE,
  )
    .optional()
    .label("Min Bathrooms")
    .concat(createMinMaxValidation("bathsMax")),
  bathsMax: createOptionalNumericValidationWithMax(
    "Bathrooms",
    BEDS_BATHS_MAX_VALUE,
  )
    .optional()
    .label("Max Bathrooms"),
  livingSpaceMin: createOptionalNumericValidationWithMax(
    "Living Space",
    LIVING_SPACE_MAX_VALUE,
  )
    .optional()
    .label("Min Living Space")
    .concat(createMinMaxValidation("livingSpaceMax")),
  livingSpaceMax: createOptionalNumericValidationWithMax(
    "Living Space",
    LIVING_SPACE_MAX_VALUE,
  )
    .optional()
    .label("Max Living Space"),
  lotSizeMin: createOptionalNumericValidationWithMax(
    "Lot Size",
    LOT_SIZE_MAX_VALUE,
  )
    .optional()
    .label("Min Lot Size")
    .concat(createMinMaxValidation("lotSizeMax")),
  lotSizeMax: createOptionalNumericValidationWithMax(
    "Lot Size",
    LOT_SIZE_MAX_VALUE,
  )
    .optional()
    .label("Max Lot Size"),
  garageSpacesMin: createOptionalNumericValidationWithMax(
    "Garage Spaces",
    GARAGE_SPACES_MAX_VALUE,
  )
    .optional()
    .label("Min Garage Spaces")
    .concat(createMinMaxValidation("garageSpacesMax")),
  garageSpacesMax: createOptionalNumericValidationWithMax(
    "Garage Spaces",
    GARAGE_SPACES_MAX_VALUE,
  )
    .optional()
    .label("Max Garage Spaces"),
  yearBuiltMin: createOptionalNumericValidationWithMax(
    "Year Built",
    new Date().getFullYear(),
  )
    .optional()
    .label("Min Year Built")
    .concat(createMinMaxValidation("yearBuiltMax")),
  yearBuiltMax: createOptionalNumericValidationWithMax(
    "Year Built",
    new Date().getFullYear(),
  )
    .optional()
    .label("Max Year Built"),

  neighborhood: yup.string().optional().label("Neighborhood"),
  city: yup.string().optional().label("City"),
  state: yup.string().optional().label("State"),
  county: yup.string().optional().label("County"),

  listDateStart: yup.date().nullable().optional().label("List Start Date"),
  listDateEnd: yup.date().nullable().optional().label("List End Date"),
  soldDateStart: yup.date().nullable().optional().label("Sold Start Date"),
  soldDateEnd: yup.date().nullable().optional().label("Sold End Date"),

  doorman: yup.string().optional().label("Doorman"),
  waterfrontClassification: yup
    .mixed<FindListingsAv8RequestWaterfrontClassificationEnum>()
    .oneOf(Object.values(FindListingsAv8RequestWaterfrontClassificationEnum))
    .nullable()
    .optional()
    .label("Waterfront Classification"),

  page: yup.number().min(1).optional().label("Page"),
  limit: yup.number().min(1).max(100).optional().label("Limit"),
  sort: yup
    .object()
    .shape({
      criteria: yup.string().required(),
      asc: yup.boolean().required(),
    })
    .optional()
    .label("Sort"),
});

export type FindListingsFiltersFormSchema =
  typeof findListingsFiltersFormSchema.__outputType;

export type MapFilter =
  | {
      shape: "circle";
      center: { lat: number; lng: number };
      /**
       * Radius in meters
       */
      radius: number;
    }
  | {
      shape: "polygon";
      points: Array<{ lat: number; lng: number }>;
    }
  | {
      shape: "none";
    };

export const INITIAL_MAP_FILTERS: MapFilter = { shape: "none" };

export const INITIAL_FORM_FILTERS = {
  dealType: "Sale" as "Sale" | "Lease",
  propertyTypes: [] as string[],
  propertyStatus: [] as string[],

  priceMin: "",
  priceMax: "",

  bedsMin: "",
  bedsMax: "",
  bathsMin: "",
  bathsMax: "",

  livingSpaceMin: "",
  livingSpaceMax: "",
  lotSizeMin: "",
  lotSizeMax: "",

  garageSpacesMin: "",
  garageSpacesMax: "",
  yearBuiltMin: "",
  yearBuiltMax: "",

  neighborhood: "",
  city: "",
  state: "",
  streetAndNumber: "",
  street: "",
  zipCode: "",
  county: "",
  district: "",
  subDistrict: "",
  schoolDistrict: "",

  listDateStart: null as Date | null,
  listDateEnd: null as Date | null,
  soldDateStart: null as Date | null,
  soldDateEnd: null as Date | null,

  doorman: "",
  waterfrontClassification:
    null as FindListingsAv8RequestWaterfrontClassificationEnum | null,

  page: 1,
  limit: 20,
};

export const INITIAL_GEOGRAPHICAL_FILTERS = {
  neighborhood: INITIAL_FORM_FILTERS.neighborhood,
  city: INITIAL_FORM_FILTERS.city,
  state: INITIAL_FORM_FILTERS.state,
  streetAndNumber: INITIAL_FORM_FILTERS.streetAndNumber,
  street: INITIAL_FORM_FILTERS.street,
  zipCode: INITIAL_FORM_FILTERS.zipCode,
  county: INITIAL_FORM_FILTERS.county,
  district: INITIAL_FORM_FILTERS.district,
  subDistrict: INITIAL_FORM_FILTERS.subDistrict,
  schoolDistrict: INITIAL_FORM_FILTERS.schoolDistrict,
};

const INITIAL_PAGINATION_OPTION = {
  limit: 10,
  page: 1,
};

export const FIND_LISTINGS_FORM_MODEL_DEFAULT_VALUES = {
  ...INITIAL_FORM_FILTERS,
  ...INITIAL_PAGINATION_OPTION,
  sort: {
    criteria: INITIAL_SORT_OPTION.key,
    asc: INITIAL_SORT_OPTION.initialDirection === "asc",
  },
  mapFilters: INITIAL_MAP_FILTERS as MapFilter,
};

export type FindListingsSchema = Partial<
  typeof FIND_LISTINGS_FORM_MODEL_DEFAULT_VALUES & {
    mapFilters: MapFilter;
  }
>;
